<template>
  <div>
    <!-- eslint-disable max-len -->
    <div
      v-if="!showSimplifyExperience"
      if="fallback-success-page-banner"
    >
      <div id="teleported-header" />
      <div id="teleported-progressbar" />
      <div id="teleported-confirm-order-subheader" />
      <div id="teleported-confirm-order-default-banner" />
    </div>
    <div id="amp-success-page-variant-simplify" />
    <main
      id="main"
      ref="main"
      :class="{'confirm-order desktop u-container': true,
               'ivs-failed': failedIvsVerification }"
    >
      <div id="exp-banner" />
      <h1
        id="main-header"
        class="font-bold"
      >
        Official USPS<sup>®</sup> Change-of-Address
      </h1>
      <IndividualProgressBarWithSteps
        v-if="showSimplifyExperience"
        :mobile="false"
      />
      <IndividualProgressBar
        v-if="!showSimplifyExperience"
        role="progressbar"
        class="c-progress-container"
        :aria-valuenow="progressStep"
        :aria-valuemin="current"
        :aria-valuemax="1"
      />
      <section id="content">
        <h2
          id="confirm-order-subheader"
          class="mt-8"
        >
          Thank you for submitting your Change-of-Address!
        </h2>
        <template v-if="mcoa">
          <p class="mt-4">
            There is one more step to complete: check your email.<br>
            You'll receive an email confirmation from us soon. In the email, click the provided link to continue and finalize your Change-of-Address request.
          </p>
        </template>
        <template v-else>
          <p
            id="confirm-order-default-banner"
            class="mt-4"
          >
            <template v-if="showCfpsId">
              Your confirmation code is <strong ref="cfpsId">{{ cfpsId | formatCfpsId }}</strong>. Please retain this Confirmation Code as it is required to view, edit or cancel your Change-of-Address.
            </template>
            <span class="confirmation-sub-text">
              You will receive a confirmation email shortly with more information. See below for more information and next steps about your Change-of-Address.
            </span>
          </p>
          <div
            v-show="flagAccessHandoffEnabled"
            id="amp-success-page"
            class="mt-8"
          >
            <div
              id="amp-offers"
              ref="offers"
            >
              <OffersBiz v-if="offersBizSegment" />
              <OffersIndFamTemp v-else />
            </div>
          </div>
          <!-- Access Handoff Disabled -->
          <div
            v-if="!flagAccessHandoffEnabled"
            ref="accessDisabled"
            class="access-disabled mt-8"
          >
            <div class="flex">
              <div class="access-disabled__img-wrapper">
                <img
                  :src="$options.MyMoveLogoPNG"
                  alt="MYMOVE logo"
                  class="access-disabled__img"
                >
              </div>
              <div class="p-10 bg-gray-100">
                <p class="access-disabled__heading">
                  Next, tackle everything on your moving to-do list with MYMOVE
                </p>
                <p class="access-disabled__copy mt-4">
                  MYMOVE is an authorized affiliate of the USPS® that connects more than 5 million people each year to deals and resources that make moving easier.
                </p>
                <a
                  id="continue-mymove"
                  href="https://www.mymove.com/moving/"
                  class="c-btn mt-6"
                >
                  Continue
                </a>
              </div>
            </div>
            <p class="access-disabled__legal mt-6">
              By clicking CONTINUE you will be leaving USPS.com and redirected to the MYMOVE website. Once you leave, you will be subject to the MYMOVE Privacy Policy and Terms and Conditions.
            </p>
            <div class="c-hairline mt-6" />
          </div>
        </template>
        <div class="mt-8 text-center">
          <router-link
            id="change-another-address"
            ref="changeAnotherAddress"
            :to="{ name: 'disclaimer' }"
            class="font-bold text-blue-200"
          >
            Need to change another address?
          </router-link>
        </div>
        <template v-if="!mcoa">
          <div
            id="address-change-important-notice"
            class="important__notice"
          >
            <h3
              id="important__notice--header"
              class="mt-8 font-bold"
            >
              <img
                id="important-notice-icon"
                alt="important notice icon"
                hidden
              >
              Important Information About Your Change-of-Address Request:
            </h3>
            <ol
              id="important-notice-list"
              ref="list"
              class="list-decimal mt-4 px-4"
            >
              <li v-if="showCfpsId">
                <strong>You should receive a mailed communication from the U.S. Postal Service at your new address
                  within 7-10 business days of your mail forwarding date.</strong> Please retain this Official
                Change-of-Address Confirmation page for your records as local agencies and/or resources may
                require it for proof of your move.
              </li>
              <li class="mt-4  font-bold">
                Please retain this Confirmation Code as it is required to view, edit, or cancel your Change-of-Address.
              </li>
              <li class="mt-4">
                <strong>You will receive a Move Validation Letter at your old address.</strong> For your protection, this will not contain your new address.
              </li>
            </ol>
            <div
              class="important__notice--link"
              hidden
            >
              <router-link
                id="change-another-address"
                ref="changeAnotherAddress"
                :to="{ name: 'disclaimer' }"
                class="change__another__address--link"
              >
                Need to change another address?
              </router-link>
            </div>
          </div>
        </template>
      </section>
    </main>
  </div>
</template>

<script>
import {
  IndividualProgressBarWithSteps,
  IndividualProgressBar,
} from '@mgo/ui/lib/components';

import Cookies from 'js-cookie';
import { desktopAnalytics } from './analytics';
import confirmOrderMixin from './mixin';

import OffersIndFamTemp from '../../splits/ConfirmOrder/DesktopOffersIndFamTemp.vue';
import OffersBiz from '../../splits/ConfirmOrder/DesktopOffersBiz.vue';

import MyMoveLogoPNG from '../../assets/images/_logo-mymove-yellow.png';
import OverstockLogoPNG from '../../assets/images/_logo-overstock.png';
import VoteBadgePNG from '../../assets/images/vote-badge.png';

export default {
  name: 'ConfirmOrder',
  components: {
    OffersIndFamTemp,
    OffersBiz,
    IndividualProgressBarWithSteps,
    IndividualProgressBar,
  },
  mixins: [confirmOrderMixin],
  data() {
    return {
      farewellShown: false,
      showFarewell: false,
    };
  },
  computed: {
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    analytics() {
      return desktopAnalytics();
    },
    userNeedsReprompt() {
      return !this.autofill
        && !this.repromptShown
        && !this.farewellShown
        && !this.showReprompt
        && !this.showFarewell;
    },
  },
  async mounted() {
    if (!Cookies.get('ampersend-user-id')) {
      const url = window.location.pathname;
      const uuid = url.substring(url.lastIndexOf('/') + 1);
      Cookies.set('ampersend-user-id', uuid);
    }
    if (!this.cfpsId && !this.dcoa) {
      this.$router.push({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
      return;
    }
    if (this.flagAmpersendEnabled && this.flagAccessHandoffEnabled) {
      this.$amp.veil(this.$refs.main);
    }
    await this.hydrateCOA();
    if (this.flagAmpersendEnabled && this.flagAccessHandoffEnabled) {
      await this.getAmpersendExperiences();
      this.$amp.unveil(this.$refs.main);
    }
    this.$store.commit('DISABLE_LOADER');

    if (this.cfpsId !== '') {
      this.$coa.reset();
      this.$deleteSession();
      Cookies.remove('coa');
      Cookies.remove('coa_complete');
      localStorage.removeItem('ampersend_data');
    }
  },

  MyMoveLogoPNG,
  OverstockLogoPNG,
  VoteBadgePNG,
};
</script>
